import type { TextRun } from '@/api/types/stowed-element'
import type { ShapeTextFonts } from '../types/shape-text-layout-state'

/**
 * CSS font properties for one painted run (a glyph span, a line strut, a drop cap). Each falls
 * back to the frame's own default when the run does not name a family or font.
 */

/** Quoted CSS family for `familyId`, or `fallback` when it is unset or unknown to the font set. */
export function familyCss(
  familyId: number | null | undefined,
  fonts: ShapeTextFonts,
  fallback: string
): string {
  return familyId && fonts?.getFamilyCssName(familyId)
    ? `"${fonts.getFamilyCssName(familyId)}", sans-serif`
    : fallback
}

/** 700/400 from the font variant's bold flag, or `fallback` when the run names no font. */
export function variantWeight<T>(
  fontId: number | null | undefined,
  fonts: ShapeTextFonts,
  fallback: T
): number | T {
  return fontId != null ? (fonts?.getVariantByFontId(fontId)?.bold ? 700 : 400) : fallback
}

/** italic/normal from the font variant's italic flag, or `fallback` when the run names no font. */
export function variantStyle<T>(
  fontId: number | null | undefined,
  fonts: ShapeTextFonts,
  fallback: T
): string | T {
  return fontId != null
    ? fonts?.getVariantByFontId(fontId)?.italic
      ? 'italic'
      : 'normal'
    : fallback
}

/** `text-decoration` for a run's underline/strikethrough, or undefined when it has neither. */
export function textDecorationCss(run: TextRun | null | undefined): string | undefined {
  return (
    [run?.underline && 'underline', run?.strikethrough && 'line-through']
      .filter(Boolean)
      .join(' ') || undefined
  )
}
